import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheck, UserPlus, X, Bell, LogOut, FlaskConical, Eye, Database,
  ExternalLink, Download, Search, ArrowRight, Building2, User, Phone, Globe, Mail, CalendarDays,
} from "lucide-react";
import { api, startGoogleLogin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePreview } from "@/context/PreviewContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

/* ---------------- primitives (light Electric-Blue) ---------------- */
const LIGHT_SURFACE = "border-[#E4E7EC] bg-white text-[#171C23]";

const PILL = {
  active: "text-emerald-600 bg-emerald-500/12",
  paid: "text-emerald-600 bg-emerald-500/12",
  invited: "text-amber-600 bg-amber-500/14",
  onboarding: "text-amber-600 bg-amber-500/14",
  pending: "text-amber-600 bg-amber-500/14",
  payment_due: "text-amber-600 bg-amber-500/14",
  rejected: "text-red-600 bg-red-500/10",
  goose_ready: "text-[#2563EB] bg-[#2563EB]/10",
  locked: "text-[#2563EB] bg-[#2563EB]/10",
  closed: "text-[#5B6472] bg-[#5B6472]/10",
  billed: "text-[#5B6472] bg-[#5B6472]/10",
  unlocked: "text-[#5B6472] bg-[#5B6472]/10",
};
function Pill({ status, testId }) {
  const label = (status || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${PILL[status] || "text-[#5B6472] bg-[#5B6472]/10"}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" /> {label}
    </span>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E4E7EC]" style={{ borderTopColor: "var(--p-accent)" }} />
    </div>
  );
}

/* ---------------- header + nav ---------------- */
const NAV = [
  { key: "producers", label: "Producers" },
  { key: "prospects", label: "Partner Prospects" },
  { key: "events", label: "Events" },
  { key: "alerts", label: "Alerts" },
];

function AdminHeader({ section, setSection }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="p-header z-30">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-4 sm:gap-6 sm:px-6">
        <button onClick={() => setSection("producers")} data-testid="admin-logo"
          className="font-display text-xl font-extrabold tracking-tight text-white" style={{ letterSpacing: "0.02em" }}>
          GOOSE<span style={{ color: "var(--p-accent)" }}>.</span>
        </button>
        <span className="rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]"
          style={{ borderColor: "rgba(255,255,255,0.16)", color: "var(--p-header-muted)" }}>Admin</span>
        <nav className="order-3 flex w-full items-center gap-4 overflow-x-auto text-sm sm:order-none sm:w-auto sm:gap-6">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`tab-${n.key}`} onClick={() => setSection(n.key)}
              className={`p-navlink shrink-0 ${section === n.key ? "active" : ""}`}>
              {n.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-sm sm:block" style={{ color: "var(--p-header-muted)" }}>{user?.email}</span>
          <div className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: "var(--p-accent)" }}>
            {(user?.name || user?.email || "A")[0].toUpperCase()}
          </div>
          <button data-testid="operator-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-semibold transition-colors sm:px-3.5"
            style={{ border: "1px solid rgba(255,255,255,0.16)", color: "var(--p-header-muted)" }}>
            <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </div>
    </header>
  );
}

/* ---------------- invite dialog ---------------- */
function InviteDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", business_name: "", contact_name: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.email || !form.business_name) { toast.error("Email and business name required"); return; }
    setSaving(true);
    try {
      await api.post("/operator/producers/invite", form);
      toast.success("CRM approved — activation invite sent");
      qc.invalidateQueries({ queryKey: ["op-producers"] });
      setOpen(false);
      setForm({ email: "", business_name: "", contact_name: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="invite-producer-btn" className="p-btn px-5">
          <UserPlus className="mr-1.5 h-4 w-4" /> Approve &amp; invite producer
        </Button>
      </DialogTrigger>
      <DialogContent className={`goose-producer ${LIGHT_SURFACE}`}>
        <DialogHeader><DialogTitle className="font-display text-xl">Approve a producer</DialogTitle>
          <DialogDescription className="text-sm text-[#5B6472]">Simulates CRM approval and sends a Goose activation invite. The producer still completes onboarding to become Active.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label className="mb-1.5 block text-xs text-[#5B6472]">Producer email</Label><Input data-testid="invite-email-input" value={form.email} onChange={set("email")} placeholder="producer@business.com" /></div>
          <div><Label className="mb-1.5 block text-xs text-[#5B6472]">Business name</Label><Input data-testid="invite-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Afterglow Collective" /></div>
          <div><Label className="mb-1.5 block text-xs text-[#5B6472]">Contact name</Label><Input data-testid="invite-contact-input" value={form.contact_name} onChange={set("contact_name")} placeholder="Sam Lee" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="invite-submit-btn" disabled={saving} onClick={submit} className="p-btn w-full">
            {saving ? "Sending…" : "Approve & send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- demo / preview tools (dev only) ---------------- */
function PreviewToolbar() {
  const { user } = useAuth();
  const qc = useQueryClient();
  if (!user?.preview_mode_enabled) return null;
  const origin = window.location.origin;
  const loadDemo = async () => {
    try { await api.post("/dev/seed"); toast.success("Demo data loaded"); qc.invalidateQueries(); }
    catch { toast.error("Seeding unavailable"); }
  };
  return (
    <div className="p-card mb-6 p-5" data-testid="preview-toolbar">
      <div className="flex items-center gap-2 text-amber-600">
        <FlaskConical className="h-4 w-4" />
        <p className="text-xs font-semibold uppercase tracking-widest">Test / demo tools</p>
      </div>
      <p className="mt-2 text-sm text-[#5B6472]">Load seeded data and walk the guest flows. Disabled in production.</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button data-testid="load-demo-btn" onClick={loadDemo} className="p-btn-ghost">
          <Database className="mr-1.5 h-4 w-4" /> Load demo data
        </Button>
        <a data-testid="guest-attribution-link" href={`${origin}/e/rooftopmix`} target="_blank" rel="noreferrer"
          className="p-btn-ghost inline-flex items-center px-4 py-2 text-sm">
          <ExternalLink className="mr-1.5 h-4 w-4" /> Guest attribution demo
        </a>
        <a data-testid="guest-night-link" href={`${origin}/n/neonfriday`} target="_blank" rel="noreferrer"
          className="p-btn-ghost inline-flex items-center px-4 py-2 text-sm">
          <ExternalLink className="mr-1.5 h-4 w-4" /> Guest event-night demo
        </a>
      </div>
    </div>
  );
}

/* ---------------- producer detail drawer ---------------- */
function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[#E4E7EC] py-3 last:border-0">
      <span className="flex items-center gap-2.5 text-sm text-[#5B6472]"><Icon className="h-4 w-4 text-[#8A94A3]" /> {label}</span>
      <span className="text-right text-sm font-semibold">{value || <span className="font-normal text-[#8A94A3]">Not set</span>}</span>
    </div>
  );
}

function ProducerDrawer({ producer, onClose, onViewAs, onReject }) {
  const p = producer;
  const canReject = p.status !== "rejected" && p.status !== "active";
  return (
    <>
      <div className="fixed inset-0 z-40 bg-[#10182e]/45" onClick={onClose} data-testid="drawer-scrim" />
      <aside data-testid="producer-drawer"
        className="goose-producer fixed right-0 top-0 z-50 h-full w-full max-w-[460px] overflow-y-auto bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#E4E7EC] px-6 py-5">
          <div>
            <h3 className="font-display text-xl font-bold">{p.business_name || "—"}</h3>
            <div className="mt-2"><Pill status={p.status} /></div>
          </div>
          <button data-testid="drawer-close" onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E4E7EC] text-[#5B6472] hover:text-[#171C23]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-10 pt-5">
          <Button data-testid="detail-view-as-btn" onClick={() => onViewAs(p)} className="p-btn w-full py-6 text-base">
            <Eye className="mr-2 h-5 w-5" /> View as producer <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <p className="mt-2 text-center text-xs font-semibold text-[#8A94A3]">Read-only preview · your Admin identity stays active</p>

          <p className="mt-7 text-xs font-semibold uppercase tracking-[0.16em] text-[#8A94A3]">Organization</p>
          <div className="mt-2">
            <DetailRow icon={User} label="Contact" value={p.contact_name || p.name} />
            <DetailRow icon={Mail} label="Email" value={p.email} />
            <DetailRow icon={Phone} label="Phone" value={p.phone} />
            <DetailRow icon={Globe} label="Website" value={p.website} />
            <DetailRow icon={Building2} label="Producer ID" value={<span className="font-mono-g text-xs">{p.producer_id}</span>} />
          </div>

          <p className="mt-7 text-xs font-semibold uppercase tracking-[0.16em] text-[#8A94A3]">Activity</p>
          <div className="mt-2">
            <DetailRow icon={CalendarDays} label="Events" value={String(p.event_count ?? 0)} />
            <DetailRow icon={ShieldCheck} label="Approved" value={p.invited_at ? new Date(p.invited_at).toLocaleDateString() : (p.created_at ? new Date(p.created_at).toLocaleDateString() : null)} />
          </div>

          {canReject && (
            <>
              <p className="mt-7 text-xs font-semibold uppercase tracking-[0.16em] text-[#8A94A3]">Operator actions</p>
              <button data-testid={`drawer-reject-${p.producer_id}`} onClick={() => onReject(p.producer_id)}
                className="mt-2 w-full rounded-full border border-[#E4E7EC] py-2.5 text-sm font-semibold text-[#5B6472] transition-colors hover:border-red-400 hover:text-red-500">
                Reject application
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/* ---------------- producers ---------------- */
const PRODUCER_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "invited", label: "Invited" },
  { key: "onboarding", label: "Onboarding" },
  { key: "rejected", label: "Rejected" },
];

function RowActions({ p, onViewAs, onOpen, onReject, canViewAs }) {
  return (
    <div className="flex items-center justify-end gap-2">
      {canViewAs && (
        <button data-testid={`preview-${p.producer_id}`} onClick={() => onViewAs(p)}
          className="p-btn-soft inline-flex items-center gap-1.5 px-3 py-1.5 text-xs">
          <Eye className="h-3.5 w-3.5" /> View as producer
        </button>
      )}
      <button data-testid={`open-producer-${p.producer_id}`} onClick={() => onOpen(p)}
        className="p-btn-ghost px-3 py-1.5 text-xs">Open</button>
    </div>
  );
}

function ProducersTab() {
  const { user } = useAuth();
  const { startPreview } = usePreview();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const canViewAs = user?.role === "operator";

  const { data, isLoading } = useQuery({
    queryKey: ["op-producers"],
    queryFn: async () => (await api.get("/operator/producers")).data,
  });

  const reject = async (id) => {
    try { await api.post(`/operator/producers/${id}/reject`); toast.success("Producer rejected"); qc.invalidateQueries({ queryKey: ["op-producers"] }); setSelected(null); }
    catch { toast.error("Failed"); }
  };
  const viewAs = (p) => { startPreview(p); navigate("/producer"); };

  const producers = useMemo(() => data?.producers || [], [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return producers.filter((p) => {
      if (filter !== "all" && p.status !== filter) return false;
      if (!q) return true;
      return (p.business_name || "").toLowerCase().includes(q)
        || (p.name || "").toLowerCase().includes(q)
        || (p.email || "").toLowerCase().includes(q);
    });
  }, [producers, query, filter]);

  if (isLoading) return <Spinner />;

  return (
    <div>
      <PreviewToolbar />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A94A3]" />
          <Input data-testid="producers-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by business, contact or email…" className="pl-9" />
        </div>
        <InviteDialog />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {PRODUCER_FILTERS.map((f) => (
          <button key={f.key} data-testid={`producer-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "text-[#2563EB]" : "p-muted border border-[#E4E7EC]"}`}
            style={filter === f.key ? { background: "var(--p-accent-soft)" } : undefined}>
            {f.label}
          </button>
        ))}
      </div>

      {/* desktop table */}
      <div className="mt-5 hidden overflow-hidden p-card md:block">
        <table className="w-full text-left text-sm" data-testid="producers-table">
          <thead>
            <tr className="border-b border-[#E4E7EC] text-xs font-semibold uppercase tracking-wider text-[#8A94A3]">
              <th className="p-4">Business</th><th className="p-4">Contact</th><th className="p-4">Status</th><th className="p-4">Events</th><th className="p-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.producer_id} data-testid={`producer-row-${p.producer_id}`} className="border-b border-[#E4E7EC]/70 last:border-0 hover:bg-[#FAFBFC]">
                <td className="p-4"><p className="font-semibold">{p.business_name || "—"}</p><p className="text-xs text-[#8A94A3]">{p.producer_id}</p></td>
                <td className="p-4 text-[#5B6472]">{p.name || "—"}<br /><span className="text-xs text-[#8A94A3]">{p.email}</span></td>
                <td className="p-4"><Pill status={p.status} /></td>
                <td className="p-4 font-mono-g text-[#5B6472]">{p.event_count}</td>
                <td className="p-4"><RowActions p={p} onViewAs={viewAs} onOpen={setSelected} onReject={reject} canViewAs={canViewAs} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-10 text-center text-sm text-[#8A94A3]">No matching producers.</p>}
      </div>

      {/* mobile cards */}
      <div className="mt-5 space-y-4 md:hidden">
        {filtered.map((p) => (
          <div key={p.producer_id} data-testid={`producer-card-${p.producer_id}`} className="p-card p-5">
            <div className="flex items-start justify-between gap-3">
              <div><p className="font-semibold">{p.business_name || "—"}</p><p className="text-xs text-[#8A94A3]">{p.name} · {p.email}</p></div>
              <Pill status={p.status} />
            </div>
            <p className="mt-3 text-sm text-[#5B6472]"><span className="font-semibold text-[#171C23]">{p.event_count}</span> events</p>
            <div className="mt-4 flex gap-2">
              {canViewAs && (
                <button data-testid={`preview-m-${p.producer_id}`} onClick={() => viewAs(p)} className="p-btn-soft inline-flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs">
                  <Eye className="h-3.5 w-3.5" /> View as producer
                </button>
              )}
              <button data-testid={`open-producer-m-${p.producer_id}`} onClick={() => setSelected(p)} className="p-btn-ghost px-3 py-2 text-xs">Details</button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="p-card p-8 text-center text-sm text-[#8A94A3]">No matching producers.</p>}
      </div>

      <p className="mt-4 text-sm text-[#5B6472]">Showing {filtered.length} of {producers.length} producers</p>

      {selected && (
        <ProducerDrawer producer={selected} onClose={() => setSelected(null)} onViewAs={viewAs} onReject={reject} />
      )}
    </div>
  );
}

/* ---------------- events ---------------- */
const EVENT_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "goose_ready", label: "Goose Ready" },
  { key: "closed", label: "Closed" },
];

function EventsTab() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["op-events"],
    queryFn: async () => (await api.get("/operator/events")).data,
  });
  const events = useMemo(() => data?.events || [], [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((ev) => {
      if (filter === "active" && ev.status !== "active") return false;
      if (filter === "goose_ready" && ev.status !== "goose_ready") return false;
      if (filter === "closed" && !["closed", "billed"].includes(ev.status)) return false;
      if (!q) return true;
      return (ev.name || "").toLowerCase().includes(q) || (ev.goose_id || "").toLowerCase().includes(q);
    });
  }, [events, query, filter]);

  if (isLoading) return <Spinner />;
  return (
    <div>
      <div className="relative sm:max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A94A3]" />
        <Input data-testid="events-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by event name or Goose ID…" className="pl-9" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {EVENT_FILTERS.map((f) => (
          <button key={f.key} data-testid={`events-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "text-[#2563EB]" : "p-muted border border-[#E4E7EC]"}`}
            style={filter === f.key ? { background: "var(--p-accent-soft)" } : undefined}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5 hidden overflow-hidden p-card md:block">
        <table className="w-full text-left text-sm" data-testid="op-events-table">
          <thead>
            <tr className="border-b border-[#E4E7EC] text-xs font-semibold uppercase tracking-wider text-[#8A94A3]">
              <th className="p-4">Event</th><th className="p-4">Goose ID</th><th className="p-4">Status</th><th className="p-4">Surveyed</th><th className="p-4">Billing</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((ev) => (
              <tr key={ev.event_id} className="border-b border-[#E4E7EC]/70 last:border-0 hover:bg-[#FAFBFC]">
                <td className="p-4 font-semibold">{ev.name}</td>
                <td className="p-4 font-mono-g text-[#5B6472]">{ev.goose_id}</td>
                <td className="p-4"><Pill status={ev.status} /></td>
                <td className="p-4 font-mono-g text-[#5B6472]">{ev.surveyed}</td>
                <td className="p-4"><Pill status={ev.billing_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-10 text-center text-sm text-[#8A94A3]">No matching events.</p>}
      </div>

      <div className="mt-5 space-y-4 md:hidden">
        {filtered.map((ev) => (
          <div key={ev.event_id} className="p-card p-5">
            <div className="flex items-start justify-between gap-3">
              <div><p className="font-semibold">{ev.name}</p><p className="font-mono-g text-xs text-[#8A94A3]">{ev.goose_id}</p></div>
              <Pill status={ev.status} />
            </div>
            <div className="mt-3 flex gap-6 text-sm text-[#5B6472]">
              <span><span className="font-semibold text-[#171C23]">{ev.surveyed}</span> surveyed</span>
              <span>Billing <Pill status={ev.billing_status} /></span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="p-card p-8 text-center text-sm text-[#8A94A3]">No matching events.</p>}
      </div>

      <p className="mt-4 text-sm text-[#5B6472]">Showing {filtered.length} of {events.length} events</p>
    </div>
  );
}

/* ---------------- alerts ---------------- */
function AlertsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["op-alerts"],
    queryFn: async () => (await api.get("/operator/alerts")).data,
  });
  if (isLoading) return <Spinner />;
  const alerts = data?.alerts || [];
  return (
    <div className="space-y-3" data-testid="alerts-list">
      {alerts.length === 0 && <div className="p-card p-10 text-center text-sm text-[#8A94A3]">No alerts. All systems nominal.</div>}
      {alerts.map((a, i) => (
        <div key={i} className="p-card flex items-start gap-3 p-4">
          <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-red-500/10 text-red-500"><Bell className="h-4 w-4" /></div>
          <div>
            <p className="font-semibold">{a.subject}</p>
            <p className="text-sm text-[#5B6472]">{a.message}</p>
            <p className="mt-1 text-xs text-[#8A94A3]">{new Date(a.created_at).toLocaleString()} · {(a.recipients || []).join(", ")}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- prospects ---------------- */
const PROSPECT_STATUS = [
  { v: "new", label: "New" },
  { v: "contacted", label: "Contacted" },
  { v: "qualified", label: "Qualified" },
  { v: "closed", label: "Closed" },
];

function MeetingCell({ p }) {
  if (p.meeting_status === "scheduled") {
    const when = p.meeting_start_at ? new Date(p.meeting_start_at).toLocaleString() : "confirmed";
    return <span className="text-emerald-600">Scheduled — {when}</span>;
  }
  return <span className="text-[#8A94A3]">Not scheduled</span>;
}

function ProspectDetailRow({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-[#E4E7EC]/70 py-2.5 last:border-0 sm:flex-row sm:gap-4">
      <span className="w-40 shrink-0 text-xs uppercase tracking-wider text-[#8A94A3]">{label}</span>
      <span className="text-sm">{children || <span className="text-[#8A94A3]">—</span>}</span>
    </div>
  );
}

function ProspectDetail({ prospect, onClose, onChanged }) {
  const [status, setStatus] = useState(prospect.status);
  const [saving, setSaving] = useState(false);
  const p = prospect;
  const market = [p.city, p.state].filter(Boolean).join(", ");
  const utms = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
    .map((k) => (p[k] ? `${k.replace("utm_", "")}: ${p[k]}` : null)).filter(Boolean).join(" · ");

  const saveStatus = async (next) => {
    setStatus(next); setSaving(true);
    try {
      await api.patch(`/operator/prospects/${p.prospect_id}/status`, { status: next });
      toast.success("Status updated"); onChanged?.();
    } catch { toast.error("Failed to update status"); setStatus(p.status); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={`goose-producer max-h-[88vh] overflow-y-auto sm:max-w-[640px] ${LIGHT_SURFACE}`} data-testid="prospect-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{p.first_name} {p.last_name}</DialogTitle>
          <DialogDescription className="sr-only">Event Partner prospect details and status</DialogDescription>
        </DialogHeader>
        <div className="mb-3 flex items-center gap-3">
          <Label className="text-xs text-[#5B6472]">Status</Label>
          <Select value={status} onValueChange={saveStatus} disabled={saving}>
            <SelectTrigger data-testid="prospect-status-select" className="h-9 w-44 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className={`goose-producer ${LIGHT_SURFACE}`}>
              {PROSPECT_STATUS.map((s) => (<SelectItem key={s.v} value={s.v} data-testid={`status-opt-${s.v}`}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-[#8A94A3]">Contact</p>
        <ProspectDetailRow label="Email"><a href={`mailto:${p.email}`} className="text-[#2563EB] hover:underline">{p.email}</a></ProspectDetailRow>
        <ProspectDetailRow label="Phone"><a href={`tel:${p.phone_normalized || p.phone}`} className="text-[#2563EB] hover:underline">{p.phone}</a></ProspectDetailRow>
        <ProspectDetailRow label="Organization">{p.organization}</ProspectDetailRow>
        <ProspectDetailRow label="Market">{market}</ProspectDetailRow>
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-[#8A94A3]">Event business</p>
        <ProspectDetailRow label="Event URL">{p.event_url ? <a href={p.event_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2563EB] hover:underline">{p.event_url}<ExternalLink className="h-3 w-3" /></a> : null}</ProspectDetailRow>
        <ProspectDetailRow label="About their events"><span className="whitespace-pre-wrap">{p.event_description}</span></ProspectDetailRow>
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-[#8A94A3]">Lead information</p>
        <ProspectDetailRow label="Status">{PROSPECT_STATUS.find((s) => s.v === status)?.label || status}</ProspectDetailRow>
        <ProspectDetailRow label="Submitted">{p.created_at ? new Date(p.created_at).toLocaleString() : null}</ProspectDetailRow>
        <ProspectDetailRow label="Source">{p.source}</ProspectDetailRow>
        <ProspectDetailRow label="Attribution">{utms}</ProspectDetailRow>
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-[#8A94A3]">Meeting</p>
        <ProspectDetailRow label="Meeting status">{p.meeting_status === "scheduled" ? "Scheduled" : "Not scheduled"}</ProspectDetailRow>
        <ProspectDetailRow label="Scheduled time">{p.meeting_start_at ? new Date(p.meeting_start_at).toLocaleString() : null}</ProspectDetailRow>
        <ProspectDetailRow label="Booking">{p.meeting_booking_url ? <a href={p.meeting_booking_url} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline">{p.meeting_booking_id || "View booking"}</a> : (p.meeting_booking_id || null)}</ProspectDetailRow>
        <DialogFooter>
          <Button data-testid="prospect-detail-close" onClick={onClose} className="p-btn-ghost">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProspectsTab({ openId, clearOpenId }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["op-prospects"],
    queryFn: async () => (await api.get("/operator/prospects")).data,
  });
  const prospects = data?.prospects || [];

  useEffect(() => {
    if (openId && prospects.length) {
      const found = prospects.find((p) => p.prospect_id === openId);
      if (found) setSelected(found);
    }
  }, [openId, prospects]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["op-prospects"] });
  const exportFile = async (fmt) => {
    try {
      const res = await api.get(`/operator/prospects/export/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `partner_prospects.${fmt}`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (isLoading) return <Spinner />;
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-[#5B6472]">{prospects.length} prospect{prospects.length === 1 ? "" : "s"}</p>
        <div className="flex gap-2">
          <Button data-testid="export-csv-btn" onClick={() => exportFile("csv")} className="p-btn-ghost text-xs">
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button data-testid="export-json-btn" onClick={() => exportFile("json")} className="p-btn-ghost text-xs">
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export JSON
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto p-card">
        <table className="w-full min-w-[920px] text-sm" data-testid="prospects-table">
          <thead>
            <tr className="border-b border-[#E4E7EC] text-left text-xs uppercase tracking-widest text-[#8A94A3]">
              <th className="p-4">Date</th><th className="p-4">Name</th><th className="p-4">Organization</th>
              <th className="p-4">Market</th><th className="p-4">Email</th><th className="p-4">Phone</th>
              <th className="p-4">Status</th><th className="p-4">Meeting</th>
            </tr>
          </thead>
          <tbody>
            {prospects.map((p) => (
              <tr key={p.prospect_id} data-testid={`prospect-row-${p.prospect_id}`} onClick={() => setSelected(p)}
                className="cursor-pointer border-b border-[#E4E7EC]/70 last:border-0 hover:bg-[#FAFBFC]">
                <td className="p-4 text-[#5B6472]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-4 font-semibold">{p.first_name} {p.last_name}</td>
                <td className="p-4 text-[#5B6472]">{p.organization}</td>
                <td className="p-4 text-[#5B6472]">{[p.city, p.state].filter(Boolean).join(", ")}</td>
                <td className="p-4 text-[#5B6472]">{p.email}</td>
                <td className="p-4 text-[#5B6472]">{p.phone}</td>
                <td className="p-4"><Pill status={p.status} /></td>
                <td className="p-4"><MeetingCell p={p} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {prospects.length === 0 && <p className="p-10 text-center text-sm text-[#8A94A3]">No prospects yet.</p>}
      </div>
      {selected && (
        <ProspectDetail prospect={selected} onClose={() => { setSelected(null); clearOpenId?.(); }} onChanged={refresh} />
      )}
    </div>
  );
}

/* ---------------- shell ---------------- */
export default function OperatorConsole() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [openProspectId, setOpenProspectId] = useState(() => new URLSearchParams(window.location.search).get("prospect"));
  const [section, setSection] = useState(openProspectId ? "prospects" : "producers");

  useEffect(() => {
    if (!loading && user && user.role !== "operator") navigate("/producer", { replace: true });
  }, [user, loading, navigate]);

  const clearProspectParam = () => {
    setOpenProspectId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("prospect");
    window.history.replaceState({}, "", url);
  };

  if (loading) return <div className="goose-producer p-workspace min-h-screen"><Spinner /></div>;
  if (!user) {
    return (
      <div className="goose-producer p-workspace flex min-h-screen items-center justify-center px-6">
        <div className="p-card max-w-md p-8 text-center">
          <p className="font-display text-3xl font-extrabold tracking-tight p-accent-text">GOOSE<span className="p-muted">.</span></p>
          <h2 className="font-display mt-5 text-2xl font-bold">Admin sign in</h2>
          <p className="mt-2 text-sm p-muted">Sign in with an authorized HAEVN admin account.</p>
          <Button data-testid="operator-signin-btn" onClick={() => startGoogleLogin("/admin")} className="p-btn mt-6 w-full py-6">Sign in with Google</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="goose-producer p-workspace min-h-screen">
      <AdminHeader section={section} setSection={setSection} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] p-subtle">HAEVN internal</p>
        <h1 className="font-display mt-2 flex items-center gap-3 text-3xl font-bold sm:text-4xl">
          <ShieldCheck className="h-8 w-8" style={{ color: "var(--p-accent)" }} /> Admin console
        </h1>
        <div className="mt-8">
          {section === "producers" && <ProducersTab />}
          {section === "prospects" && <ProspectsTab openId={openProspectId} clearOpenId={clearProspectParam} />}
          {section === "events" && <EventsTab />}
          {section === "alerts" && <AlertsTab />}
        </div>
      </div>
    </div>
  );
}
